// Contact.js
import React, { useState, useEffect, useRef } from 'react';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    instagram: '',
    kakaotalk: '',
    message: '',
    privacyConsent: false,
    latitude: null,
    longitude: null,
    address: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [gpsError, setGpsError] = useState(null);
  const [countdown, setCountdown] = useState(null);

  // Reference for Naver maps
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const naverMapRef = useRef(null);

  // Replace with your actual Google Apps Script Web App URL
  const FORM_ENDPOINT = 'https://script.google.com/macros/s/AKfycbyF_6VsUjjEdK2xomHZE5BKWDA5rHK4HUrj8WQCD6-dtNjKk20tn3zEO-ZJ9-D2Pmnw/exec';

  // Replace with your Naver Map API Client ID
  // const NAVER_CLIENT_ID = 'gek2l0mck9'; // Need to replace this with a valid client ID
  const NAVER_CLIENT_ID = 'gek2l0mck9'; // Need to replace this with a valid client ID

  useEffect(() => {
    // Load Naver Maps API script
    const script = document.createElement('script');
    // Adding submodules=geocoder to include geocoding functionality
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${NAVER_CLIENT_ID}&submodules=geocoder`;
    script.async = true;
    script.onload = () => console.log("Naver Maps API loaded");
    script.onerror = () => console.error("Failed to load Naver Maps API");
    document.head.appendChild(script);

    return () => {
      // Attempt to clean up script if component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'privacyConsent' && submitStatus === '개인정보 수집 및 이용에 동의해주셔야 합니다.') {
      setSubmitStatus(null);
    }
  };

  // Auto-hide non-success error messages after 2 seconds
  useEffect(() => {
    let timer;
    if (submitStatus && submitStatus !== 'success') {
      timer = setTimeout(() => {
        setSubmitStatus(null);
      }, 2000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [submitStatus]);

  // Success message with countdown timer
  useEffect(() => {
    let timer;
    let interval;
    if (submitStatus === 'success') {
      setCountdown(5);

      // Update countdown every second
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      // Reset submitStatus after 5 seconds
      timer = setTimeout(() => {
        setSubmitStatus(null);
        setCountdown(null);
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [submitStatus]);

  // Prevent body scroll when privacy policy popup is open
  useEffect(() => {
    if (showPrivacyPolicy) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showPrivacyPolicy]);

  // Initialize map when showMap becomes true and coordinates exist
  useEffect(() => {
    // Function to reverse geocode (convert coordinates to address)
    const reverseGeocode = (lat, lng) => {
      if (!window.naver) return;

      setGpsStatus('fetching');
      setGpsError(null);

      try {
        // Fix: Use correct parameter format for reverseGeocode
        window.naver.maps.Service.reverseGeocode({
          location: new window.naver.maps.LatLng(lat, lng),
        }, function (status, response) {
          if (status !== window.naver.maps.Service.Status.OK) {
            setGpsError('주소를 찾을 수 없습니다.');
            setGpsStatus('error');
            return;
          }

          const result = response.result;
          const items = result.items;

          if (items && items.length > 0) {
            const address = items[0].address;
            setFormData(prevState => ({
              ...prevState,
              address: address
            }));
            setGpsStatus('success');
          } else {
            setGpsError('주소를 찾을 수 없습니다.');
            setGpsStatus('error');
          }
        });
      } catch (error) {
        console.error("Error in reverse geocoding:", error);
        setGpsError('주소 변환 중 오류가 발생했습니다.');
        setGpsStatus('error');
      }
    };

    // Function to move marker and update address
    const moveMarkerAndUpdateAddress = (lat, lng) => {
      if (markerRef.current && naverMapRef.current) {
        markerRef.current.setPosition(new window.naver.maps.LatLng(lat, lng));
        setFormData(prevState => ({
          ...prevState,
          latitude: lat,
          longitude: lng
        }));

        // Get address for new position
        reverseGeocode(lat, lng);
      }
    };

    // Function to initialize Naver Map
    const initializeMap = (lat, lng) => {
      try {
        if (!window.naver) {
          console.error("Naver Maps API not loaded yet");
          return;
        }

        // Create map instance if it doesn't exist
        if (!naverMapRef.current) {
          naverMapRef.current = new window.naver.maps.Map(mapRef.current, {
            center: new window.naver.maps.LatLng(lat, lng),
            zoom: 15,
            zoomControl: true,
            zoomControlOptions: {
              position: window.naver.maps.Position.TOP_RIGHT
            }
          });

          // Create a marker
          markerRef.current = new window.naver.maps.Marker({
            position: new window.naver.maps.LatLng(lat, lng),
            map: naverMapRef.current,
            draggable: true
          });

          // Add click event to map
          window.naver.maps.Event.addListener(naverMapRef.current, 'click', function (e) {
            moveMarkerAndUpdateAddress(e.coord.lat(), e.coord.lng());
          });

          // Add dragend event to marker
          window.naver.maps.Event.addListener(markerRef.current, 'dragend', function () {
            const position = markerRef.current.getPosition();
            moveMarkerAndUpdateAddress(position.lat(), position.lng());
          });
        } else {
          // Just update map center and marker position
          naverMapRef.current.setCenter(new window.naver.maps.LatLng(lat, lng));
          markerRef.current.setPosition(new window.naver.maps.LatLng(lat, lng));
        }

        // Get address for the location
        reverseGeocode(lat, lng);
      } catch (error) {
        console.error("Error initializing map:", error);
        setGpsError("지도를 초기화하는 중 오류가 발생했습니다.");
        setGpsStatus('error');
      }
    };

    if (showMap && window.naver && mapRef.current) {
      if (formData.latitude && formData.longitude) {
        initializeMap(formData.latitude, formData.longitude);
      } else {
        // Default to Seoul if no coordinates
        initializeMap(37.5665, 126.9780);
      }
    } else if (!showMap) {
      // Reset refs when map is closed so it re-initializes correctly next time
      naverMapRef.current = null;
      markerRef.current = null;
    }
  }, [showMap, formData.latitude, formData.longitude]);

  // Function to handle address search
  const handleAddressSearch = () => {
    if (!window.naver || !formData.address) return;

    setGpsStatus('fetching');
    setGpsError(null);

    try {
      window.naver.maps.Service.geocode({
        query: formData.address
      }, function (status, response) {
        if (status !== window.naver.maps.Service.Status.OK) {
          setGpsError('입력하신 주소를 찾을 수 없습니다.');
          setGpsStatus('error');
          return;
        }

        const result = response.v2;
        const items = result.addresses;

        if (items.length > 0) {
          const lat = parseFloat(items[0].y);
          const lng = parseFloat(items[0].x);

          setFormData(prevState => ({
            ...prevState,
            latitude: lat,
            longitude: lng,
            address: items[0].roadAddress || items[0].jibunAddress
          }));

          if (naverMapRef.current && markerRef.current) {
            naverMapRef.current.setCenter(new window.naver.maps.LatLng(lat, lng));
            markerRef.current.setPosition(new window.naver.maps.LatLng(lat, lng));
          }

          setGpsStatus('success');

          // Ensure map is visible when address is searched
          if (!showMap) setShowMap(true);
        } else {
          setGpsError('입력하신 주소를 찾을 수 없습니다.');
          setGpsStatus('error');
        }
      });
    } catch (error) {
      console.error("Error in geocoding:", error);
      setGpsError('주소 검색 중 오류가 발생했습니다.');
      setGpsStatus('error');
    }
  };

  // Function to get current location
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setGpsStatus('unavailable');
      setGpsError('브라우저에서 위치 정보 기능을 지원하지 않습니다.');
      return;
    }

    setGpsStatus('fetching');
    setGpsError(null);
    setFormData(prevState => ({
      ...prevState,
      latitude: null,
      longitude: null,
      address: ''
    }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        setFormData(prevState => ({
          ...prevState,
          latitude: lat,
          longitude: lng
        }));

        // Show map after getting location
        setShowMap(true);

        // The initializeMap effect will handle map setup
        // The reverseGeocode inside will update the address

        setGpsStatus('success');
        console.log("Location acquired:", lat, lng);
      },
      (error) => {
        console.error("Error getting location:", error);
        setFormData(prevState => ({
          ...prevState,
          latitude: null,
          longitude: null,
          address: ''
        }));

        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGpsStatus('permission_denied');
            setGpsError('위치 정보 접근 권한이 거부되었습니다. 브라우저 설정을 확인해주세요.');
            break;
          case error.POSITION_UNAVAILABLE:
            setGpsStatus('unavailable');
            setGpsError('현재 위치 정보를 가져올 수 없습니다.');
            break;
          case error.TIMEOUT:
            setGpsStatus('error');
            setGpsError('위치 정보를 가져오는데 시간이 초과되었습니다.');
            break;
          default:
            setGpsStatus('error');
            setGpsError('위치 정보를 가져오는 중 오류가 발생했습니다.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Toggle map visibility
  const toggleMap = () => {
    setShowMap(!showMap);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacyConsent) {
      setSubmitStatus('개인정보 수집 및 이용에 동의해주셔야 합니다.');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);

    const dataToSend = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      instagram: formData.instagram.trim(),
      kakaotalk: formData.kakaotalk.trim(),
      message: formData.message.trim(),
      latitude: formData.latitude,
      longitude: formData.longitude,
      address: formData.address ? formData.address.trim() : '',
    };

    console.log("Submitting data:", dataToSend);

    try {
      await fetch(FORM_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      setSubmitStatus('success');
      // Reset form
      setFormData({
        name: '',
        phone: '',
        instagram: '',
        kakaotalk: '',
        message: '',
        privacyConsent: false,
        latitude: null,
        longitude: null,
        address: ''
      });
      setGpsStatus('idle');
      setGpsError(null);
      setShowMap(false);

    } catch (error) {
      console.error('Error submitting form (Network Error):', error);
      setSubmitStatus('제출 중 오류가 발생했습니다. 네트워크 연결을 확인하거나 잠시 후 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPrivacyPolicy = (e) => {
    e.preventDefault();
    setShowPrivacyPolicy(true);
  };

  const closePrivacyPolicy = () => {
    setShowPrivacyPolicy(false);
  };

  return (
    <section id="contact">
      <div className="container">
        <h2>무료 견적 및 상담 문의</h2>
        <p style={{ textAlign: 'center' }}>궁금한 점이 있으시면 언제든지 편하게 연락주세요. 친절하게 상담해 드립니다.</p>

        <form className="contact-form" onSubmit={handleSubmit}>
          {/* Form Groups */}
          <div className="form-group">
            <label htmlFor="name">이름/호칭 <span className="required">*</span></label>
            <input type="text" id="name" name="name" autoComplete="name" value={formData.name} onChange={handleChange} required disabled={isSubmitting} />
          </div>

          <div className="form-group">
            <label htmlFor="phone">연락처 <span className="required">*</span></label>
            <input type="tel" id="phone" name="phone" autoComplete="tel" value={formData.phone} onChange={handleChange} placeholder="예: 010-1234-5678" required disabled={isSubmitting} />
          </div>

          <div className="form-group">
            <label htmlFor="instagram">인스타그램 ID (선택)</label>
            <input type="text" id="instagram" name="instagram" autoComplete="off" value={formData.instagram} onChange={handleChange} placeholder="예: viewtylab" disabled={isSubmitting} />
          </div>

          <div className="form-group">
            <label htmlFor="kakaotalk">카카오톡 ID (선택)</label>
            <input type="text" id="kakaotalk" name="kakaotalk" autoComplete="off" value={formData.kakaotalk} onChange={handleChange} placeholder="예: viewtylab" disabled={isSubmitting} />
          </div>

          {/* Location Input Group */}
          <div className="form-group location-group">
            <label htmlFor="address">주소 (선택)</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="예: 서울시 강남구 역삼동"
                disabled={isSubmitting}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleAddressSearch}
                disabled={isSubmitting || !formData.address}
                className="cta-button secondary"
                style={{ padding: '8px 15px', fontSize: '0.9rem' }}
              >
                주소 검색
              </button>
            </div>
          </div>

          {/* GPS and Map Controls */}
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <button
              type="button"
              onClick={handleGetLocation}
              disabled={gpsStatus === 'fetching' || isSubmitting}
              className="cta-button secondary"
              style={{ flexShrink: 0, padding: '8px 15px', fontSize: '0.9rem' }}
            >
              {gpsStatus === 'fetching' ? '위치 찾는 중...' : '현재 위치 가져오기'}
            </button>

            <button
              type="button"
              onClick={toggleMap}
              disabled={isSubmitting}
              className="cta-button secondary"
              style={{ flexShrink: 0, padding: '8px 15px', fontSize: '0.9rem' }}
            >
              {showMap ? '지도 닫기' : '지도 보기'}
            </button>

            <div className="gps-status" style={{ fontSize: '0.85rem', color: '#555', flexGrow: 1, minWidth: '150px' }}>
              {gpsStatus === 'success' && (
                <span style={{ color: 'green' }}>
                  ✓ 위치 정보 추가됨 (주소: {formData.address || '주소 변환 중...'})
                </span>
              )}
              {gpsError && <span style={{ color: 'red' }}>! {gpsError}</span>}
              {gpsStatus === 'idle' && <span>정확한 견적을 위해 위치를 추가할 수 있습니다.</span>}
            </div>
          </div>

          {/* Map Container */}
          {showMap && (
            <div className="map-container" style={{ marginBottom: '20px', borderRadius: '8px', overflow: 'hidden' }}>
              <div
                ref={mapRef}
                style={{ width: '100%', height: '300px', border: '1px solid #ddd', borderRadius: '8px' }}
              ></div>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                * 핀을 드래그하거나 지도를 클릭하여 위치를 조정할 수 있습니다.
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="message">문의 내용 <span className="required">*</span></label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="예: 아파트 32평 창문 청소 견적 문의합니다. (외부, 내부, 확장여부, 층수, 위치 등)"
              required
              disabled={isSubmitting}
            ></textarea>
          </div>

          {/* Privacy Consent Checkbox */}
          <div className="form-group privacy-consent" style={{
            fontSize: '0.85rem',
            position: 'relative',
            paddingLeft: '28px',
            minHeight: '24px'
          }}>
            <input
              type="checkbox"
              id="privacyConsent"
              name="privacyConsent"
              checked={formData.privacyConsent}
              onChange={handleChange}
              disabled={isSubmitting}
              required
              style={{
                position: 'absolute',
                left: '0',
                top: '4px',
                width: '16px',
                height: '16px',
                cursor: 'pointer'
              }}
            />
            <label
              htmlFor="privacyConsent"
              style={{
                lineHeight: '1.5',
                cursor: 'pointer',
                display: 'inline-block'
              }}
            >
              개인정보 수집 및 이용에 동의합니다. <span className="required">*</span>
              <button type="button" onClick={openPrivacyPolicy} style={{ marginLeft: '5px', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', fontSize: 'inherit' }}>(개인정보처리방침 보기)</button>
            </label>
          </div>

          {/* Privacy Notice */}
          <div className="privacy-notice" style={{ fontSize: '0.8rem', color: '#666', marginTop: '10px', marginBottom: '20px', paddingLeft: '5px', borderLeft: '3px solid #eee' }}>
            <p style={{ margin: '2px 0' }}>※ 수집하는 개인정보 항목: 이름, 연락처, SNS ID(선택), 위치정보/주소(선택)</p>
            <p style={{ margin: '2px 0' }}>※ 수집 목적: 문의 접수 및 상담, 정확한 견적 제공</p>
            <p style={{ margin: '2px 0' }}>※ 보유 기간: 문의 처리 완료 후 3개월</p>
          </div>

          {/* Status Messages */}
          <div className="form-status-messages">
            {submitStatus === 'success' &&
              <p className={'success-message form-success-message-animated'}>
                문의가 접수되었습니다. 곧 맑은뷰연구소에서 연락드리겠습니다!{countdown !== null && ` (${countdown}초)`}
              </p>
            }
            {submitStatus && submitStatus !== 'success' &&
              <p className="error-message">
                {submitStatus}
              </p>
            }
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`cta-button ${submitStatus === 'success' ? 'button-hidden' : ''}`}
            style={{ minWidth: '150px', margin: '0 auto', display: 'block' }}
          >
            {isSubmitting ? '전송 중...' : '문의 보내기'}
          </button>
        </form>

        {/* Contact Info */}
        <div className="contact-info">
          <p><strong>전화 문의:</strong> <a href="tel:010-2158-5768">010-2158-5768</a></p>
          <p><strong>이메일:</strong> <a href="mailto:viewtylab@kakao.com">viewtylab@kakao.com</a></p>
          <p><strong>네이버블로그:</strong> <a href="https://blog.naver.com/viewtylab" target="_blank" rel="noopener noreferrer">https://blog.naver.com/viewtylab</a></p>
          <p><strong>카톡:</strong> <a href="https://pf.kakao.com/_ZxdBfn" target="_blank" rel="noopener noreferrer">@viewtylab</a></p>
          <p><strong>인스타:</strong> <a href="https://instagram.com/viewtylab" target="_blank" rel="noopener noreferrer">@viewtylab</a></p>
          <p><strong>유튜브:</strong> <a href="https://www.youtube.com/@viewtylab" target="_blank" rel="noopener noreferrer">@viewtylab</a></p>
          <p><strong>상담 가능 시간:</strong> 평일 09:00 ~ 18:00</p>
        </div>
      </div>

      {/* Privacy Policy Popup */}
      {showPrivacyPolicy && (
        <div
          className="privacy-policy-popup"
          onClick={closePrivacyPolicy}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1050
          }}
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '30px', borderRadius: '8px',
              maxWidth: '90%', width: '600px', maxHeight: '85vh',
              overflowY: 'auto', position: 'relative', boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
            }}
          >
            <button onClick={closePrivacyPolicy} aria-label="개인정보처리방침 닫기" style={{ position: 'absolute', top: '10px', right: '15px', background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888', padding: '5px', lineHeight: '1' }}> × </button>
            <h2 style={{ marginTop: '0', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>개인정보 처리방침</h2>
            <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
              {/* Privacy Policy Text */}
              <p>맑은뷰연구소('https://맑은뷰연구소.com'이하 '맑은뷰연구소')은(는) 「개인정보 보호법」 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.</p>
              <h3>제1조(개인정보의 처리 목적)</h3>
              <p>맑은뷰연구소는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
              <ol><li>견적 상담 및 문의 접수<p>상담 접수, 요청사항 확인, 견적 제공 (필요 시 위치정보 활용), 맞춤형 상담 및 서비스 제공을 위한 연락 등의 목적으로 개인정보를 처리합니다.</p></li></ol>
              <h3>제2조(개인정보의 처리 및 보유 기간)</h3>
              <p>① 맑은뷰연구소는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
              <p>② 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
              <ul><li>견적 상담 및 문의 접수 : 상담 처리 완료 후 3개월</li></ul>
              <h3>제3조(처리하는 개인정보의 항목)</h3>
              <p>맑은뷰연구소는 다음의 개인정보 항목을 처리하고 있습니다.</p>
              <ul>
                <li>필수항목 : 이름, 연락처, 문의내용</li>
                <li>선택항목 : 인스타그램 ID, 카카오톡 ID, 위치정보(위도, 경도, 주소)</li>
              </ul>
              <h3>제4조(개인정보의 파기절차 및 파기방법)</h3>
              <p>① 맑은뷰연구소는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
              <p>② 정보주체로부터 동의받은 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도 불구하고 다른 법령에 따라 개인정보를 계속 보존하여야 하는 경우에는, 해당 개인정보를 별도의 데이터베이스(DB)로 옮기거나 보관장소를 달리하여 보존합니다.</p>
              <p>③ 개인정보 파기의 절차 및 방법은 다음과 같습니다.</p>
              <ol><li>파기절차 : 맑은뷰연구소는 파기 사유가 발생한 개인정보를 선정하고, 개인정보 보호책임자의 승인을 받아 개인정보를 파기합니다.</li><li>파기방법 : 전자적 파일 형태의 정보는 기록을 재생할 수 없는 기술적 방법을 사용합니다. 종이에 출력된 개인정보는 분쇄기로 분쇄하거나 소각을 통하여 파기합니다.</li></ol>
              <h3>제5조(정보주체와 법정대리인의 권리·의무 및 그 행사방법)</h3>
              <p>① 정보주체는 맑은뷰연구소에 대해 언제든지 개인정보 열람·정정·삭제·처리정지 요구 등의 권리를 행사할 수 있습니다.</p>
              <p>② 제1항에 따른 권리 행사는 맑은뷰연구소에 대해 「개인정보 보호법」 시행령 제41조제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며 맑은뷰연구소는 이에 대해 지체 없이 조치하겠습니다.</p>
              <p>③ 제1항에 따른 권리 행사는 정보주체의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수 있습니다. 이 경우 "개인정보 처리 방법에 관한 고시(제2020-7호)" 별지 제11호 서식에 따른 위임장을 제출하셔야 합니다.</p>
              <p>④ 개인정보 열람 및 처리정지 요구는 「개인정보 보호법」 제35조 제4항, 제37조 제2항에 의하여 정보주체의 권리가 제한 될 수 있습니다.</p>
              <p>⑤ 개인정보의 정정 및 삭제 요구는 다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 경우에는 그 삭제를 요구할 수 없습니다.</p>
              <p>⑥ 맑은뷰연구소는 정보주체 권리에 따른 열람의 요구, 정정·삭제의 요구, 처리정지의 요구 시 열람 등 요구를 한 자가 본인이거나 정당한 대리인인지를 확인합니다.</p>
              <h3>제6조(개인정보 보호책임자)</h3>
              <p>① 맑은뷰연구소는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
              <ul><li>개인정보 보호책임자<ul><li>성명 : 관리자</li><li>연락처 : 010-2158-5768, viewtylab@kakao.com</li></ul></li></ul>
              <p>② 정보주체께서는 맑은뷰연구소의 서비스를 이용하시면서 발생한 모든 개인정보 보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보 보호책임자에게 문의하실 수 있습니다. 맑은뷰연구소는 정보주체의 문의에 대해 지체 없이 답변 및 처리해드릴 것입니다.</p>
              <h3>제7조(개인정보 자동 수집 장치의 설치·운영 및 거부에 관한 사항)</h3>
              <p>맑은뷰연구소는 정보주체의 이용정보를 저장하고 수시로 불러오는 '쿠키(cookie)'를 사용하지 않습니다.</p>
              <h3>제8조(개인정보 안전성 확보 조치)</h3>
              <p>맑은뷰연구소는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
              <ol><li>관리적 조치 : 내부관리계획 수립·시행, 정기적 직원 교육</li><li>기술적 조치 : 개인정보처리시스템 등의 접근권한 관리, 접근통제시스템 설치, 고유식별정보 등의 암호화, 보안프로그램 설치</li><li>물리적 조치 : 전산실, 자료보관실 등의 접근통제</li></ol>
              <h3>제9조(개인정보처리방침 변경)</h3>
              <p>① 이 개인정보처리방침은 2025년 4월 28일부터 적용됩니다.</p>
            </div>
            <div style={{ marginTop: '25px', textAlign: 'right', borderTop: '1px solid #eee', paddingTop: '15px' }}>
              <button onClick={closePrivacyPolicy} className="cta-button" style={{ backgroundColor: '#6c757d' }}> 닫기 </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Contact;